/* eslint react-hooks/exhaustive-deps: "warn" */
import { useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './SalesProspectingLeadGenerationExtension.module.scss';

type Translate = (key: string) => string;

type Tab = {
  num: string;
  title: string;
  description: string;
};

const getTabs = (t: Translate): Tab[] => [
  {
    num: '01',
    title: t('Check Company Activity Status'),
    description: t(
      'Verify if a company is active by viewing its registration number, VAT, and tax ID. This ensures you are dealing with legitimate and operational businesses.',
    ),
  },
  {
    num: '02',
    title: t('Up to 20 Years of Company Financials'),
    description: t(
      'Access comprehensive financial data spanning up to 20 years. Analyze historical financial performance to understand the company’s stability and growth prospects.',
    ),
  },
  {
    num: '03',
    title: t('Shareholders & Group Structure'),
    description: t(
      'Understand the group structure of a company, including subsidiaries and affiliated entities. See who has significant control and ownership, and view the company’s wider business network.',
    ),
  },
  {
    num: '04',
    title: t('Credit Score and Limit Recommendation'),
    description: t(
      'Instantly access credit scores and limit recommendations. This is crucial for assessing the financial health and creditworthiness of potential business partners.',
    ),
  },
  {
    num: '05',
    title: t('Key Executives Insight'),
    description: t(
      'View and filter key executives in any company. Identify decision-makers and view contact information, such as email and phone number, to streamline your networking and outreach efforts.',
    ),
  },
  {
    num: '06',
    title: t('Send Emails and Add Contacts to Sequences'),
    description: t(
      'Send emails and add contacts to automated sequences. This feature enhances your email marketing efforts by allowing for personalized and efficient communication flows.',
    ),
  },
  {
    num: '07',
    title: t('View Data on Any Website, Including LinkedIn'),
    description: t(
      'Access detailed company data while browsing any website, including LinkedIn. This integration ensures you always have relevant information at your fingertips, regardless of where you are online.',
    ),
  },
];

export const SalesProspectingLeadGenerationExtension = () => {
  const { t } = useInt();
  const tabs = getTabs(t);
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.marker}>
          <span className={styles.markerNum}>07</span> / {t('Extension')}
        </div>

        <div className={styles.body}>
          <div className={styles.head}>
            <TitleBlock
              align="left"
              title={t('Transform the way you access business information')}
              description={t(
                'Whether you are browsing LinkedIn, a corporate website, or any other platform, our extension delivers critical data seamlessly.',
              )}
            />
          </div>

          <div className={styles.layout}>
            <div className={styles.side}>
              <div className={styles.tabs}>
                {tabs.map((tab, index) => {
                  const isOpen = index === openIndex;

                  return (
                    <button
                      key={tab.num}
                      type="button"
                      className={cn(styles.tab, isOpen && styles.tabOn)}
                      aria-expanded={isOpen}
                      onClick={() => setOpenIndex(index)}
                    >
                      <span className={styles.tabNum}>{tab.num}</span>
                      <span className={styles.tabTitle}>{tab.title}</span>
                      <span className={styles.tabDescription}>{tab.description}</span>
                    </button>
                  );
                })}
              </div>

              <div className={styles.actions}>
                <Button
                  type="blue"
                  size="lg"
                  href="https://platform.globaldatabase.com/public/signup/sales_engagement_free_tier"
                >
                  {t('Add the extension, free')}
                </Button>

                <Button type="dark-outline" size="lg" href="/book-a-demo">
                  {t('Request a demo')}
                </Button>
              </div>
            </div>

            <div className={styles.stage}>
              <div className={styles.stageGlow} aria-hidden="true" />

              <div className={styles.panel}>
                <div className={styles.panelBar}>
                  <span className={styles.panelDots}>
                    <span className={cn(styles.panelDot, styles.dotRed)} />
                    <span className={cn(styles.panelDot, styles.dotAmber)} />
                    <span className={cn(styles.panelDot, styles.dotGreen)} />
                  </span>

                  <span className={styles.panelUrl}>globaldatabase.com/extension</span>

                  <span className={styles.panelTag}>{t('SAMPLE')}</span>
                </div>

                <div className={styles.panelBody}>
                  <div className={styles.panelCompanyRow}>
                    <span className={styles.panelAvatar}>N</span>

                    <div className={styles.panelCompanyInfo}>
                      <span className={styles.panelCompany}>Nordlicht Systeme GmbH</span>
                      <span className={styles.panelReg}>HRB 214 887 · Handelsregister</span>
                    </div>

                    <span className={styles.panelStatus}>Active</span>
                  </div>

                  <div className={styles.panelRows}>
                    <div className={styles.panelRow}>
                      <span>VAT</span>
                      <b>DE 214 887 003</b>
                    </div>

                    <div className={styles.panelRow}>
                      <span>Employees</span>
                      <b>11–50</b>
                    </div>

                    <div className={styles.panelRow}>
                      <span>Credit score</span>
                      <span className={styles.panelScore}>
                        B<i>Modelled</i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <p className={styles.stageCaption}>{t('The extension panel, in place on a live page.')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
